import { formatMonthYear } from '@/lib/date';

import type { CropYear, MapField } from './schemas';

/**
 * The panel's second line (Figma `787:678791`).
 *
 * The frame reads `Plano, Dallas | 24 Acres` — city, city. The contract settles it as **City, State**
 * (the live API returns `"Dallas, TX | 124.55 Acres"`), so the design copy is the error, not the
 * contract (spec § Contract, D11).
 *
 * Either half of the place may be null — reverse-geocoding finds no incorporated place for open
 * country — so the parts that exist are joined and the ones that do not simply do not appear. A
 * subtitle reading `, TX | 24 Acres` would be worse than one reading `TX | 24 Acres`.
 *
 * No rounding: `acreage` is already a number on the wire, and interpolation renders `24.0` as `24`
 * and keeps `61.5`'s half.
 */
export function fieldSubtitle({
  city,
  state,
  acreage,
}: Pick<MapField, 'city' | 'state' | 'acreage'>): string {
  const place = [city, state].filter(Boolean).join(', ');
  const acres = `${acreage} Acres`;
  return place ? `${place} | ${acres}` : acres;
}

/**
 * The device-count chip (Figma `787:678795`).
 *
 * **`null` is not zero.** The Device layer fails on its own (113586 §15b/§15c), and a Field that then
 * reads `0 Devices` tells the Grower something false about their account. A dash says "we do not
 * know" — the distinction that reads as trivial and is not.
 *
 * Figma's chip reads `12 Device`. Pluralised here: the singular after 12 is a typo, not copy.
 */
export function deviceCountLabel(count: number | null): string {
  const value = count === null ? '—' : String(count);
  return `${value} ${count === 1 ? 'Device' : 'Devices'}`;
}

/**
 * The Crop chip (Figma `787:678798`), or `null` where the Field has neither a Crop nor a Crop Year —
 * **absent, not empty** (grilling Q11; root `CONTEXT.md`).
 *
 * Two arguments because the wire carries two independent fields: either can be set without the
 * other, so one combined object would need a half-null shape. Months render `MMM YYYY` through
 * `lib/date` per ADR-0007 — never dayjs directly; a range of one renders once.
 *
 * A **blank** Crop name is treated as no Crop at all, not as a Crop called `''`: `crop` is
 * `z.string().nullable()` with no floor, so `crop ?? season` would let an empty string shadow a Crop
 * Year that IS recorded, and would return `''` where this function promises `null`.
 */
export function cropLabel(crop: string | null, cropYear: CropYear | null): string | null {
  const season = cropYear ? formatCropYear(cropYear) : null;
  const name = crop?.trim();
  if (!name) return season;
  return season ? `${name} (${season})` : name;
}

/**
 * A crop year as one display string — "Jan 2025 - Jan 2026", or a single month when the two ends
 * agree. Exported because Device Details renders the season on its own, apart from the crop name,
 * where the Map's chip renders the two together via {@link cropLabel}.
 */
export function formatCropYear({ start, end }: CropYear): string {
  const from = formatMonthYear(start);
  const to = formatMonthYear(end);
  if (!from) return to ?? '';
  return !to || from === to ? from : `${from} - ${to}`;
}

/**
 * The 113586 §1f notice: a Field whose Boundary could not be drawn is skipped, every other Field
 * renders, and this names the casualties.
 *
 * FE-drafted copy in the shipped house voice (`We couldn't …`) — no Figma frame covers this notice.
 *
 * The caller unions **two** sources into the list it passes: the server's `meta.skipped` (Fields the
 * API could not serialise) and `partitionDrawableFields`'s own `skipped` (Fields whose geometry
 * parsed but cannot be drawn). One notice, either way — the Grower does not care which layer failed
 * to read the polygon.
 */
export function skippedFieldsMessage(names: readonly string[]): string {
  if (names.length === 0) return '';
  if (names.length === 1) {
    const name = names[0]?.trim();
    return name
      ? `We couldn't display the boundary for ${name}.`
      : "We couldn't display one Field's boundary.";
  }
  return `We couldn't display ${names.length} Field boundaries.`;
}

/**
 * Decimal places a coordinate is shown to. Five is ~1.1 m at these latitudes — finer than a Grower
 * can place a corner by eye, and coarse enough that a stacked latitude/longitude pair stays
 * readable. **Display only:** the Field Mapping panel's coordinate ROWS are deliberately full
 * precision, because an untouched row is re-plotted verbatim by the panel's own Save and rounding it
 * would move a Point nobody edited (ticket 09).
 */
export const COORDINATE_DECIMALS = 5;

/** A latitude or longitude as the Point tooltip and the coordinate rows show it — fixed width, so a
 *  stacked pair lines up. */
export function formatCoordinate(value: number): string {
  return value.toFixed(COORDINATE_DECIMALS);
}
